import banner from "../../../courses/module-1/img1.png";

const modulo1_pt = {
  courseId: "finanzas-personales",
  id: "modulo-1", // ⚠️ Mantém o mesmo ID para compatibilidade com o quiz
  title: "📘 MÓDULO 1: Introdução às Finanças Pessoais",
  estimatedDuration: "1 hora",
  format: "Leitura + atividade reflexiva + avaliação",
  bannerUrl: banner,
  sections: [
    {
      id: "objetivos",
      navLabel: "Objetivos",
      title: "🎯 Objetivos do módulo",
      content: [
        "Entender o que são finanças pessoais e por que elas são essenciais na vida adulta.",
        "Questionar mitos culturais que atrapalham nossa relação com o dinheiro.",
        "Inspirar você a começar a organizar suas finanças desde agora, com ferramentas reais e exemplos próximos da sua realidade.",
      ],
    },
    {
      id: "lectura",
      navLabel: "Leitura principal",
      title: "📖 Leitura principal: O que são finanças pessoais?",
      content: [
        {
          type: "paragraph",
          text: "As finanças pessoais são todas as decisões e ações que você toma em relação ao seu dinheiro. Não tem a ver com grandes valores nem com ser “bom de matemática”. Tem a ver com perguntas simples do dia a dia:",
        },
        {
          type: "list",
          items: [
            "Em que estou gastando meu dinheiro?",
            "Quanto preciso para pagar meus gastos fixos?",
            "Como posso juntar dinheiro para algo importante?",
            "Vai sobrar para a viagem de fim de ano?",
            "Como evitar me enrolar no cartão de crédito?",
          ],
        },
        {
          type: "paragraph",
          text: "Exemplo do dia a dia:",
        },
        {
          type: "paragraph",
          text: "Luísa, de 18 anos, mora em Recife e faz curso técnico no IFPE. Recebe uma bolsa de iniciação científica e vende brigadeiros no campus. Começou a anotar seus gastos numa planilha do Google. Descobriu que gastava cerca de R$ 90 por mês em lanches entre as aulas. Ao perceber isso, passou a levar um lanche de casa e agora guarda esse dinheiro para comprar um notebook usado.",
        },
      ],
    },
    {
      id: "importancia",
      navLabel: "Importância",
      title: "🧠 Por que isso é tão importante?",
      content: [
        {
          type: "paragraph",
          text: "Porque, se você não decidir o destino do seu dinheiro, alguém vai decidir por você: a publicidade, as redes sociais, o algoritmo, a compra por impulso…",
        },
        {
          type: "paragraph",
          text: "Cuidar das suas finanças não é só útil — é libertador. Te ajuda a:",
        },
        {
          type: "list",
          items: [
            "Reduzir o estresse financeiro",
            "Ter mais autonomia e clareza",
            "Evitar dívidas desnecessárias",
            "Planejar objetivos reais",
            "Viver com mais tranquilidade e consciência",
          ],
        },
      ],
    },
    {
      id: "mitos",
      navLabel: "Mitos comuns",
      title:
        "🧨 Mitos comuns sobre o dinheiro (e por que deixá-los para trás!)",
      content: [
        {
          type: "paragraph",
          text: "1. “Sou muito jovem para pensar nisso”",
        },
        {
          type: "list",
          items: [
            "É justamente o contrário. Quanto antes você começar, mais vantagem terá.",
            "Se você cria um hábito hoje, aos 25 já estará anos à frente de muita gente.",
          ],
        },
        {
          type: "paragraph",
          text: "2. “Eu ganho pouco, não dá para guardar nada”",
        },
        {
          type: "list",
          items: [
            "Não é sobre a quantia — é sobre o hábito.",
            "R$ 5 por semana = R$ 260 por ano.",
            "Se você aprende a guardar um pouco, aprende a guardar muito quando tiver mais.",
          ],
        },
        {
          type: "paragraph",
          text: "3. “Finanças é coisa de rico ou de especialista”",
        },
        {
          type: "list",
          items: [
            "Não é necessário investir em ações ou criptomoedas para cuidar bem do dinheiro.",
            "Basta entender o básico: entradas, saídas, crédito, poupança e prioridades.",
          ],
        },
      ],
    },
    {
      id: "historia",
      navLabel: "História inspiradora",
      title: "💬 História inspiradora de autonomia financeira ",
      content: [
        {
          type: "paragraph",
          text: "Linda Brasil: Professora, ativista e primeira mulher trans eleita no Nordeste",
        },
        {
          type: "paragraph",
          text: "Linda Brasil, sergipana, enfrentou forte discriminação desde jovem, mas transformou sua trajetória em um exemplo de organização, resistência e construção de autonomia. Antes de se tornar uma figura pública, trabalhou como professora, empreendeu pequenos projetos, buscou formação e organizou seus próprios recursos para continuar estudando e financiando ações comunitárias.",
        },
        {
          type: "paragraph",
          text: "Fundadora do Instituto Respeita, Linda coordena projetos sociais voltados à educação, alimentação, saúde e acolhimento. Para manter tudo funcionando, aprendeu na prática a administrar recursos, captar financiamentos, organizar doações, planejar custos e gerir equipes.",
        },
        {
          type: "paragraph",
          text: "O que isso tem a ver com finanças pessoais?",
        },
        {
          type: "paragraph",
          text: "Tudo. Linda mostra que finanças não são sobre riqueza, mas sobre autonomia, sobrevivência, planejamento e propósito.",
        },
        {
          type: "paragraph",
          text: "Mesmo enfrentando dificuldades, ela criou uma estrutura financeira sustentável para ajudar outras pessoas.",
        },
        {
          type: "list",
          items: [
            "“A gente aprende a fazer o dinheiro render quando ele é importante para aquilo que acreditamos.” – Linda Brasil",
          ],
        },
      ],
    },
    {
      id: "elementos-essenciais ",
      navLabel: "Elementos essenciais",
      title: "🧩 Elementos essenciais das finanças pessoais",
      content: [
        {
          type: "table",
          headers: ["Conceito", "Significado", "Exemplo no Brasil"],
          rows: [
            [
              "Renda",
              "Tudo o que entra: salário, bolsa, mesada, bicos, vendas.",
              "Bolsa PIBIC, Pix de um freela, venda de doces",
            ],
            [
              "Gastos",
              "Tudo o que sai: alimentação, transporte, lazer, estudo.",
              "Tarifa do ônibus, iFood, pacote de dados",
            ],
            [
              "Poupança",
              "Dinheiro reservado para um objetivo futuro.",
              "Guardar para comprar um celular",
            ],
            [
              "Crédito",
              "Dinheiro emprestado que será devolvido com juros.",
              "Cartão de crédito, crediário, empréstimo",
            ],
            [
              "Investimento",
              "Aplicar dinheiro para fazê-lo crescer.",
              "Tesouro Direto, CDBs, fundos simples",
            ],
            [
              "Seguro",
              "Proteção financeira contra riscos.",
              "Seguro de celular, seguro de vida estudantil",
            ],
          ],
        },
      ],
    },
    {
      id: "actividad",
      navLabel: "Atividade reflexiva",
      title: "💡 Atividade reflexiva",
      content: [
        "Responda no seu caderno ou bloco de notas:",
        {
          type: "orderedList",
          items: [
            `O que você sente quando pensa em “dinheiro”?`,
            `Qual frase sobre dinheiro você mais ouviu na sua família?`,
            `Já sentiu ansiedade por não saber se o dinheiro ia dar? Como lidou com isso?`,
            `O que você gostaria de mudar na sua relação com o dinheiro a partir de hoje?`,
          ],
        },
      ],
    },
    {
      id: "recurso",
      navLabel: "Recurso adicional",
      title: "🎬 Recurso adicional (vídeo)",
      content: [
        {
          type: "paragraph",
          text: "Video recomendado em português:",
        },
        {
          type: "link",
          href: "https://www.youtube.com/watch?v=oVicExyPvQY&list=PLYyfXNzWsiMyWG8exKAkjiCZMZlD_uKVg&index=4",
          text: "👉 Rumo aos 100 MIL: Mudança de Mentalidade e Minhas Carteira de Investimento",
        },
        {
          type: "paragraph",
          text: "Canal: Martins Educacional — Marcos Martins",
        },
        {
          type: "paragraph",
          text: "(Explica de forma simples e prática como mudar a mentalidade para iniciar-se no mundo dos investimentos)",
        },
      ],
    },
    {
      id: "lecturas",
      navLabel: "Leituras recomendadas",
      title: "📚 Leituras recomendadas",
      content: [
        {
          type: "link",
          href: "https://www.eusoubud.com.br/wp-content/uploads/2021/07/O-Homem-Mais-Rico-da-Babilonia-George-S.-Clason.pdf ",
          text: "O Homem mais Rico da Babiônia",
        },
        {
          type: "paragraph",
          text: "Livro com acesso gratuito sobre regras básicas de mudança para mentalidade invstidora.",
        },
        {
          type: "link",
          href: "https://mepoupe.com",
          text: "Blog “Me Poupe!” – Nathalia Arcuri",
        },
        {
          type: "paragraph",
          text: "Conteúdos diretos sobre organização financeira para brasileiros.",
        },
        {
          type: "link",
          href: "https://www.bcb.gov.br/cidadaniafinanceira",
          text: "Cidadania Financeira – Banco Central",
        },
        {
          type: "paragraph",
          text: "Artigos, simuladores e cartilhas oficiais.",
        },
      ],
    },
    {
      id: "quiz",
      navLabel: "Quiz",
      title: "📝 Miniquiz de autoavaliação",
      questions: [
        {
          id: "q1",
          question: "O que são finanças pessoais?",
          options: [
            { key: "a", text: "Apenas investimentos" },
            { key: "b", text: "Somente meus ganhos" },
            {
              key: "c",
              text: "Tudo o que faço com meu dinheiro",
            },
            { key: "d", text: "Abrir conta em banco" },
          ],
          correct: "c",
        },
        {
          id: "q2",
          question: "Qual é um mito comum?",
          options: [
            { key: "a", text: "Poupar é importante" },
            { key: "b", text: "Dinheiro “não é coisa para jovem”" },
            { key: "c", text: "Sabemos que gastos precisam ser controlados" },
            { key: "d", text: "Seguros existem para proteção" },
          ],
          correct: "b",
        },
        {
          id: "q3",
          question: "O que ajuda a ter mais tranquilidade financeira?",
          options: [
            { key: "a", text: "Comprar por impulso" },
            { key: "b", text: "Usar todo o limite do cartão" },
            { key: "c", text: "Organizar suas finanças" },
            { key: "d", text: "Ignorar suas dívidas" },
          ],
          correct: "c",
        },
        {
          id: "q4",
          question: "O que é recomendado mesmo ganhando pouco?",
          options: [
            { key: "a", text: "Investir em criptomoedas" },
            { key: "b", text: "Guardar nem que seja R$ 5" },
            { key: "c", text: "Evitar falar de dinheiro" },
            { key: "d", text: "Gastar tudo no mês" },
          ],
          correct: "b",
        },
        {
          id: "q5",
          question: "Por que começar jovem faz diferença?",
          options: [
            { key: "a", text: "Porque é obrigatório" },
            { key: "b", text: "Porque está na moda" },
            { key: "c", text: "Porque o tempo faz seu dinheiro crescer" },
            { key: "d", text: "Porque todo mundo faz" },
          ],
          correct: "c",
        },
      ],
    },
    {
      id: "bibliografia",
      navLabel: "Bibliografia",
      title: "📚 Bibliografia para aprofundar",
      content: [
        {
          type: "list",
          items: [
            "Nathalia Arcuri – “Me Poupe!: 10 passos para nunca mais faltar dinheiro”",
          ],
        },
        {
          type: "paragraph",
          text: "Guia acessível e direto para jovens brasileiros.",
        },
        {
          type: "list",
          items: ["Gustavo Cerbasi – “Como organizar sua vida financeira”"],
        },
        {
          type: "paragraph",
          text: "Livro clássico sobre orçamento, planejamento e escolhas conscientes.",
        },
        {
          type: "list",
          items: ["Tiago Reis – “Do mil ao milhão sem cortar o cafezinho”"],
        },
        {
          type: "paragraph",
          text: "Explica como construir patrimônio com hábitos realistas.",
        },
      ],
    },
    {
      id: "frase",
      navLabel: "Frase do dia",
      title: "🧠 Frase do dia",
      content: [
        "“O dinheiro não define quem você é — mas saber usá-lo define até onde você pode chegar.”",
      ],
    },
  ],
};

export default modulo1_pt;
